import React, { useState, useMemo, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Document, Page } from "react-pdf/dist/umd/entry.webpack";
import { Modal, Button, Icon, Input } from "semantic-ui-react";
import { closeModal } from "../../../app/common/modals/modalSlice";
import {
  addHistoryToDb,
  updateDocInDb,
  updateNotificationInDb,
} from "../../../app/firestore/firestoreService";
import { changePageScaleSign } from "../../../app/annots/annotSlice";
import { toast } from "react-toastify";
import {
  convertFullName,
  getAndSavePdfDimensions,
  getFormFieldValues,
} from "../../../app/common/util/util";
import { getFormFieldData } from "./docFill/formFieldData/formFieldData";
import LoadingComponent from "../../../app/layout/LoadingComponent";
import FormFieldSelector from "./docPrepare/formFields/FormFieldSelector";

const pdfOptions = {
  cMapUrl: "cmaps/",
  cMapPacked: true,
  standardFontDataUrl: "standard_fonts/",
};

export default function DocAgentSignModal({
  signatureNotificationId,
  signatureRoleKey,
}) {
  const dispatch = useDispatch();
  const { doc, docUrl } = useSelector((state) => state.doc);
  const { transaction } = useSelector((state) => state.transaction);
  const { currentUserProfile } = useSelector((state) => state.profile);
  const { pageScaleSign } = useSelector((state) => state.annot);

  const agentRepresents = transaction?.agentRepresents;
  const brokerRole =
    agentRepresents === "Buyer" ? "Buyer Broker" : "Seller Broker";
  const isAgent = transaction.userId === currentUserProfile.userId ? true : false;
  const isCoAgent = transaction.coAgentId === currentUserProfile.userId ? true : false;
  const isManager = transaction.managerId === currentUserProfile.userId ? true : false;
  const signerRoleFieldName = signatureRoleKey
    ? signatureRoleKey === "coagent"
      ? " CoAgent"
      : " Broker"
    : isAgent
    ? " Broker"
    : isCoAgent
    ? " CoAgent"
    : isManager
    ? " Manager"
    : "";
  const signerLocationKey = signatureRoleKey || (isCoAgent ? "coagent" : "agent");

  const signatureFieldName = "Signature " + agentRepresents + signerRoleFieldName;
  const dateFieldName = "Date Signature " + agentRepresents + signerRoleFieldName;

  const pageScale = pageScaleSign || 1.0;
  const signatureBoxRef = useRef(null);

  // Find the broker signature field coordinates for highlighting
  const brokerField = useMemo(() => {
    const formFieldData = getFormFieldData(doc.title, transaction);
    const formSignatureField =
      formFieldData?.find((f) => f.name === signatureFieldName) || null;
    if (formSignatureField) return formSignatureField;

    const requestedLocation = doc?.agentSignatureLocations?.[signerLocationKey];
    if (requestedLocation?.signatureFieldName === signatureFieldName) {
      return requestedLocation;
    }
    return requestedLocation || null;
  }, [doc, transaction, signatureFieldName, signerLocationKey]);

  const [numPages, setNumPages] = useState(null);
  const [pageDimensions, setPageDimensions] = useState({
    width: 612,
    height: 792,
  });
  const [showSignForm, setShowSignForm] = useState(false);
  const [signatureName, setSignatureName] = useState(
    convertFullName(currentUserProfile) || ""
  );
  const [isSigning, setIsSigning] = useState(false);
  const [formFieldValues, setFormFieldValues] = useState();

  useEffect(() => {
    getFormFieldValues(doc, transaction, setFormFieldValues);
  }, [doc, transaction]);

  useEffect(() => {
    if (!brokerField || !numPages) return;

    const scrollTimer = setTimeout(() => {
      if (signatureBoxRef.current) {
        signatureBoxRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }
    }, 250);

    return () => clearTimeout(scrollTimer);
  }, [brokerField, numPages, pageScale]);

  function onDocumentLoadSuccess(pdfObject) {
    getAndSavePdfDimensions(doc, pdfObject, setNumPages, setPageDimensions, "party");
  }

  function handleZoomIn() {
    if (pageScaleSign < 4) dispatch(changePageScaleSign(pageScaleSign * 1.25));
  }

  function handleZoomOut() {
    if (pageScaleSign > 0.5) dispatch(changePageScaleSign(pageScaleSign / 1.25));
  }

  async function handleSign() {
    if (!signatureName.trim()) {
      toast.error("Please enter your name to sign.");
      return;
    }
    let modalWillClose = false;
    setIsSigning(true);
    try {
      await updateDocInDb(doc.id, {
        [`formFieldValues.${signatureFieldName}`]: signatureName.trim(),
        [`formFieldValues.${dateFieldName}`]: new Date(),
      });
      const historyRole = transaction?.userId === currentUserProfile.id 
              ? (transaction.agentRepresents === "Buyer" 
                  ? "Buyer agent" : 
                  (transaction.agentRepresents === "Seller" 
                    ? "Listing Agent" : null)
                    
                  ) : null;

      try {
        await addHistoryToDb(
          transaction.id,
          currentUserProfile,
          "Agent signed",
          doc.name,
          null, //currentUserProfile,
          historyRole,
        );
      } catch (error) {
        console.log("silent error adding agent signed to history ", error);
      }
      if (signatureNotificationId) {
        try {
          await updateNotificationInDb(signatureNotificationId, {
            status: "resolved",
          });
        } catch (error) {
          console.log("silent error resolving signature notification ", error);
        }
      }
      toast.success("Document signed successfully!");
      modalWillClose = true;
      dispatch(closeModal());
    } catch (error) {
      toast.error(error.message);
    } finally {
      if (!modalWillClose) {
        setIsSigning(false);
      }
    }
  }

  function handleClose() {
    dispatch(closeModal());
  }

  return (
    <Modal open={true} onClose={handleClose} size="fullscreen" closeIcon>
      <Modal.Header>
        <Icon name="file pdf outline" />
        {doc.name || doc.title}
        <span
          style={{ fontSize: "14px", fontWeight: "normal", marginLeft: "12px", color: "#888" }}
        >
          — Sign as {brokerRole}
        </span>
      </Modal.Header>

      {/* Custom flex layout: scrollable PDF + fixed bottom bar */}
      <Modal.Content
        style={{
          padding: 0,
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 140px)",
          overflow: "hidden",
        }}
      >
        {/* Scrollable PDF area */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            backgroundColor: "#f3f6f8",
            padding: "16px 0",
          }}
        >
          <Document
            file={docUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={<LoadingComponent />}
            options={pdfOptions}
          >
            {Array.from(new Array(numPages), (el, index) => (
              <div
                className="pdf-page-wrapper"
                id={`page-${index}`}
                key={`page-${index}`}
              >
                <div className="pdf-page-number">
                  Page {index + 1}/{doc.numPages}
                </div>
                <div
                  className="pdf-page-container"
                  style={{
                    position: "relative",
                    height: `${pageDimensions.height * pageScale}px`,
                    width: `${pageDimensions.width * pageScale}px`,
                  }}
                >
                  <Page
                    scale={pageScale}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                    pageNumber={index + 1}
                  />
	                  {numPages &&
	                    formFieldValues?.map((formField) => (
	                      <React.Fragment key={formField.name}>
	                        {formField.page === index && (
	                          <FormFieldSelector
	                            formField={formField}
	                            pageScale={pageScale}
	                          />
	                        )}
	                      </React.Fragment>
	                    ))}
                  {brokerField && brokerField.page === index && (
                    <div
                      ref={signatureBoxRef}
	                      onClick={() => setShowSignForm(true)}
	                      title="Click here to sign"
                      style={{
                        position: "absolute",
                        top: `${(brokerField.top + brokerField.height - brokerField.fontSize) * pageScale}px`,
                        left: `${brokerField.left * pageScale}px`,
                        width: `${brokerField.width * pageScale}px`,
                        height: `${brokerField.height * pageScale}px`,
                        backgroundColor: "rgba(255, 220, 0, 0.35)",
                        border: "2px solid rgba(220, 160, 0, 0.8)",
                        borderRadius: "3px",
	                        cursor: "pointer",
                        zIndex: 10,
                      }}
                    />
                  )}
                </div>
              </div>
            ))}
          </Document>
        </div>

        {/* Fixed bottom action bar */}
        <div
          style={{
            borderTop: "1px solid #e8e8e8",
            padding: "12px 20px",
            backgroundColor: "white",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            flexShrink: 0,
          }}
        >
          <Button onClick={handleClose} disabled={isSigning}>
            Close
          </Button>

          {/* Zoom controls */}
          <Icon
            size="large"
            color="grey"
            name="minus circle"
            onClick={handleZoomOut}
            style={{ cursor: "pointer" }}
          />
          <span style={{ fontWeight: "bold", minWidth: "42px", textAlign: "center" }}>
            {Math.round(pageScale * 100)}%
          </span>
          <Icon
            size="large"
            color="grey"
            name="plus circle"
            onClick={handleZoomIn}
            style={{ cursor: "pointer" }}
          />

          <div style={{ flex: 1 }} />

          {/* Sign controls — collapsed until agent clicks */}
          {!showSignForm ? (
            <Button primary size="large" onClick={() => setShowSignForm(true)}>
              <Icon name="pencil" /> Click Here to Sign
            </Button>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {signatureName.trim() && (
                <span
                  style={{
                    fontFamily: "Brush Script MT, cursive",
                    fontSize: "22px",
                    color: "#0037b8",
                  }}
                >
                  {signatureName}
                </span>
              )}
              <Input
                value={signatureName}
                onChange={(e) => setSignatureName(e.target.value)}
                placeholder="Your full name"
                style={{ width: "200px" }}
              />
              <Button
                primary
                size="large"
                onClick={handleSign}
                loading={isSigning}
                disabled={isSigning || !signatureName.trim()}
              >
                <Icon name="check" /> Confirm Signature
              </Button>
              <Button onClick={() => setShowSignForm(false)} disabled={isSigning}>
                Cancel
              </Button>
            </div>
          )}
        </div>
      </Modal.Content>
    </Modal>
  );
}


